import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Bell, CheckCheck, AlertTriangle, AlertCircle, Loader2 } from "lucide-react";
import {
  listAlerts, markAlertsRead, resolveAlert,
} from "@/lib/delivery";
import { toast } from "sonner";

const SEV_COLOR = {
  critical: "text-rose-600",
  warning:  "text-amber-500",
  info:     "text-sky-500",
};

function timeAgo(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}z`;
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const data = await listAlerts(false, 30);
      setItems(data.items || []);
      setUnread(data.unread_count || 0);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 60_000);
    return () => clearInterval(t);
  }, [refresh]);

  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  const markAllRead = async () => {
    setLoading(true);
    try {
      await markAlertsRead();
      await refresh();
    } finally { setLoading(false); }
  };

  const onResolve = async (id) => {
    try {
      await resolveAlert(id);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          data-testid="notifications-btn"
        >
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full bg-rose-500 text-white text-[9px] font-semibold flex items-center justify-center"
              data-testid="notification-unread-count"
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96 p-0" data-testid="notifications-panel">
        <div className="flex items-center justify-between px-3 py-2 border-b">
          <div className="text-sm font-semibold">Notificări</div>
          <div className="flex items-center gap-2">
            {unread > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-[11px] px-2"
                disabled={loading}
                onClick={markAllRead}
                data-testid="mark-all-read-btn"
              >
                {loading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <CheckCheck className="h-3 w-3 mr-1" />}
                Marchează toate ca citite
              </Button>
            )}
          </div>
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          {items.length === 0 ? (
            <div className="py-12 text-center text-xs text-muted-foreground">
              Nicio notificare
            </div>
          ) : (
            items.map((a) => {
              const unreadRow = !a.read_at;
              const Icon = a.severity === "critical" ? AlertCircle : AlertTriangle;
              return (
                <div
                  key={a.id}
                  className={`px-3 py-2 border-b border-border/60 text-xs ${
                    unreadRow ? "bg-primary/5" : ""
                  }`}
                  data-testid={`alert-row-${a.id}`}
                >
                  <div className="flex items-start gap-2">
                    <Icon className={`h-3.5 w-3.5 shrink-0 mt-0.5 ${SEV_COLOR[a.severity] || "text-slate-500"}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium truncate">{a.title}</span>
                        <span className="text-[10px] text-muted-foreground">{timeAgo(a.triggered_at)}</span>
                      </div>
                      <div className="text-muted-foreground mt-0.5 leading-relaxed">{a.message}</div>
                      <div className="flex items-center gap-2 mt-1.5">
                        {a.deep_link && (
                          <Link
                            to={a.deep_link}
                            onClick={() => { markAlertsRead([a.id]); setOpen(false); }}
                            className="text-primary hover:underline"
                            data-testid={`alert-deep-link-${a.id}`}
                          >
                            Vezi detalii →
                          </Link>
                        )}
                        {!a.resolved_at && (
                          <button
                            onClick={() => onResolve(a.id)}
                            className="text-muted-foreground hover:text-emerald-600"
                            data-testid={`alert-resolve-${a.id}`}
                          >
                            Rezolvată
                          </button>
                        )}
                        {a.resolved_at && (
                          <Badge variant="outline" className="text-[10px] border-emerald-200 bg-emerald-50 text-emerald-700">
                            Rezolvată
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
